import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  TvIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

interface DisplaySize {
  label: string;
  width: string;
  height: string;
  description: string;
}

interface Environment {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: string;
  fixedFrame?: { width: string; height: string; scale: number };
  sizes?: DisplaySize[];
}

const BASE_URL = window.location.origin;

const DESKTOP_SIZES: DisplaySize[] = [
  { label: '1280', width: '1280px', height: '800px', description: 'Laptop HD' },
  { label: '1440', width: '1440px', height: '900px', description: 'Desktop HD' },
  { label: '1920', width: '1920px', height: '1080px', description: 'Full HD' },
  { label: '2560', width: '2560px', height: '1440px', description: '2K / QHD' },
];

const TV_SIZES: DisplaySize[] = [
  { label: '1280×720', width: '1280px', height: '720px', description: 'HD Ready' },
  { label: '1920×1080', width: '1920px', height: '1080px', description: 'Full HD' },
  { label: '3840×2160', width: '3840px', height: '2160px', description: '4K UHD' },
];

const ENVIRONMENTS: Environment[] = [
  {
    id: 'desktop',
    label: 'Desktop',
    description: 'Full website as seen on laptops and large monitors',
    path: '/',
    icon: <ComputerDesktopIcon className="w-6 h-6" />,
    badge: 'Default',
    badgeColor: 'bg-blue-100 text-blue-700',
    sizes: DESKTOP_SIZES,
  },
  {
    id: 'mobile',
    label: 'Mobile',
    description: 'Optimised mobile experience at /m',
    path: '/m',
    icon: <DevicePhoneMobileIcon className="w-6 h-6" />,
    badge: 'iOS / Android',
    badgeColor: 'bg-green-100 text-green-700',
    fixedFrame: { width: '390px', height: '844px', scale: 0.75 },
  },
  {
    id: 'tablet',
    label: 'Tablet',
    description: 'Tablet-optimised layout at /t',
    path: '/t',
    icon: <DeviceTabletIcon className="w-6 h-6" />,
    badge: 'iPad',
    badgeColor: 'bg-amber-100 text-amber-700',
    fixedFrame: { width: '768px', height: '1024px', scale: 0.7 },
  },
  {
    id: 'tv',
    label: 'TV Display',
    description: 'Fullscreen TV mode for smart TVs and displays at /tv',
    path: '/tv',
    icon: <TvIcon className="w-6 h-6" />,
    badge: 'Apple TV / Smart TV',
    badgeColor: 'bg-gray-100 text-gray-700',
    sizes: TV_SIZES,
  },
];

interface EnvCardProps {
  env: Environment;
  isExpanded: boolean;
  onToggle: () => void;
}

function parsePixels(val: string): number {
  return parseInt(val.replace('px', ''), 10);
}

function EnvCard({ env, isExpanded, onToggle }: EnvCardProps) {
  const fullUrl = `${BASE_URL}${env.path}`;
  const isFixed = !!env.fixedFrame;
  const hasSizes = !!env.sizes && env.sizes.length > 0;

  const [selectedSizeIdx, setSelectedSizeIdx] = useState(0);

  const activeSize = hasSizes ? env.sizes![selectedSizeIdx] : null;

  const frameW = isFixed ? env.fixedFrame!.width : activeSize?.width ?? '100%';
  const frameH = isFixed ? env.fixedFrame!.height : activeSize?.height ?? '700px';
  const fixedScale = isFixed ? env.fixedFrame!.scale : undefined;

  const containerW = parsePixels(frameW);
  const PREVIEW_MAX_WIDTH = 1100;
  const scale = fixedScale ?? (containerW > PREVIEW_MAX_WIDTH ? PREVIEW_MAX_WIDTH / containerW : 1);
  const scaledHeight = parsePixels(frameH) * scale;

  const isCentered = isFixed;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-golf-sand flex items-center justify-center text-golf-orange">
            {env.icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-golf-gray">{env.label}</h3>
              {env.badge && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${env.badgeColor}`}>
                  {env.badge}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">{env.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-golf-orange border border-golf-orange/30 bg-golf-orange/5 rounded-lg hover:bg-golf-orange hover:text-white transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            Open
          </a>
          <button
            onClick={onToggle}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isExpanded ? 'Hide' : 'Preview'}
            <ChevronDownIcon
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden border-t border-gray-100"
          >
            {hasSizes && (
              <div className="flex items-center gap-3 px-6 py-3 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">Size</span>
                {env.sizes!.map((size, i) => (
                  <button
                    key={size.label}
                    onClick={() => setSelectedSizeIdx(i)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      i === selectedSizeIdx
                        ? 'bg-golf-orange text-white border-golf-orange shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange/50 hover:text-golf-orange'
                    }`}
                  >
                    <span>{size.label}</span>
                    <span className={`${i === selectedSizeIdx ? 'text-white/70' : 'text-gray-400'}`}>
                      {size.description}
                    </span>
                  </button>
                ))}
                <span className="ml-auto text-xs text-gray-400">
                  {parsePixels(frameW)}×{parsePixels(frameH)}px
                  {scale < 1 && (
                    <span className="ml-1 text-gray-300">
                      · scaled to {Math.round(scale * 100)}%
                    </span>
                  )}
                </span>
              </div>
            )}

            <div
              className={`bg-gray-100 p-4 transition-all ${isCentered ? 'flex justify-center' : ''}`}
              style={{ minHeight: `${scaledHeight + 32}px` }}
            >
              <div
                style={{
                  width: frameW,
                  height: frameH,
                  transform: scale < 1 ? `scale(${scale})` : undefined,
                  transformOrigin: isCentered ? 'top center' : 'top left',
                  ...(scale < 1
                    ? { marginBottom: `calc(${frameH} * ${scale - 1})` }
                    : {}),
                  transition: 'width 0.3s ease, height 0.3s ease',
                }}
                className={`relative overflow-hidden shadow-xl bg-white ${
                  env.id === 'mobile'
                    ? 'rounded-[2.5rem] border-[10px] border-gray-800'
                    : env.id === 'tablet'
                    ? 'rounded-[1.5rem] border-[14px] border-gray-800'
                    : 'rounded-xl'
                }`}
              >
                {(env.id === 'mobile' || env.id === 'tablet') && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-800 rounded-b-xl z-10" />
                )}
                <iframe
                  key={`${env.id}-${frameW}-${frameH}`}
                  src={fullUrl}
                  title={`${env.label} preview`}
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2">
              <GlobeAltIcon className="w-4 h-4 text-gray-400 shrink-0" />
              <code className="text-xs text-gray-500 truncate">{fullUrl}</code>
              <button
                onClick={() => navigator.clipboard.writeText(fullUrl)}
                className="ml-auto shrink-0 text-xs text-gray-400 hover:text-golf-orange transition-colors"
              >
                Copy URL
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function PreviewEnvironments() {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = (id: string) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-golf-gray">Preview Environments</h2>
          <p className="text-gray-500 mt-1">
            Launch or preview the site across all supported device environments.
          </p>
        </div>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-golf-orange text-white text-sm font-semibold rounded-xl hover:bg-golf-orange/90 transition-colors shadow-sm"
        >
          <GlobeAltIcon className="w-4 h-4" />
          Open Live Site
        </a>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {ENVIRONMENTS.map((env) => (
          <motion.div
            key={env.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <EnvCard
              env={env}
              isExpanded={expanded === env.id}
              onToggle={() => toggle(env.id)}
            />
          </motion.div>
        ))}
      </div>

      <div className="bg-golf-sand/50 border border-golf-sand rounded-xl p-5 text-sm text-gray-600">
        <p className="font-semibold text-golf-gray mb-1">How previews work</p>
        <ul className="list-disc list-inside space-y-1 text-gray-500">
          <li>Previews load the live site inside an embedded frame — data is real and live</li>
          <li>Desktop and TV previews can be resized to common display resolutions</li>
          <li>Large resolutions are scaled down to fit — the actual iframe renders at full resolution</li>
          <li>Use <strong>Open</strong> to launch a full tab for hands-on testing or to share a link</li>
          <li>TV Display mode auto-rotates slides — press spacebar to pause when open in a tab</li>
        </ul>
      </div>
    </div>
  );
}
